import { Button } from '@/shared/components/Buttons';
import { Snackbar } from '@/shared/components/Snackbar';
import StepperPageShell from '@/shared/layout/StepperPageShell';
import CreateModal from '@/shared/layout/createLayout/CreateModal';
import { Loader } from '@/shared/components/Loader';
import { RAISE_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import {
  useCreateServiceRequest,
  type StepNum,
} from './useCreateServiceRequest';
import Step1FacilityEquipment from './Step1FacilityEquipment';
import Step2DetailsAttachments from './Step2DetailsAttachments';
import Step3Preview from './Step3Preview';

// ─── Step tab nav ─────────────────────────────────────────────────────────────

const STEPS: { num: StepNum; label: string }[] = [
  { num: 1, label: L.STEP1_LABEL },
  { num: 2, label: L.STEP2_LABEL },
  { num: 3, label: L.STEP3_LABEL },
];

const StepTabNav: React.FC<{
  currentStep: StepNum;
  maxStep: StepNum;
  onStepClick: (step: StepNum) => void;
}> = ({ currentStep, maxStep, onStepClick }) => (
  <nav className="flex flex-row w-full gap-0">
    {STEPS.map(({ num, label }, index) => {
      const isActive = num === currentStep;
      const isCompleted = num < maxStep;
      const isClickable = num <= maxStep && !isActive;
      const isDisabled = num > maxStep;
      const isLast = index === STEPS.length - 1;

      const textClass = isActive
        ? 'text-secondary-color-40'
        : isCompleted
          ? 'text-success-50'
          : 'text-neutral-60';

      const labelColor = isActive || isCompleted ? '#181A1B' : undefined;

      const borderBottomClass = isActive
        ? 'border-secondary-color-40'
        : isCompleted
          ? 'border-success-50'
          : 'border-[#DCDDE0]';

      return (
        <div
          key={num}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onClick={isClickable ? () => onStepClick(num) : undefined}
          onKeyDown={
            isClickable
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onStepClick(num);
                  }
                }
              : undefined
          }
          className={`flex flex-col flex-1 border-b-2 ${borderBottomClass} ${
            !isLast ? 'border-r border-r-[#DCDDE0]' : ''
          } ${isClickable ? 'cursor-pointer select-none' : isDisabled ? 'cursor-not-allowed' : ''}`}
        >
          <div className={`w-full font-medium py-2 ${textClass}`}>
            <span className="flex items-center justify-start gap-4">
              <span className="font-semibold text-[24px] ml-3">
                {String(num).padStart(2, '0')}
              </span>
              <span
                className="text-[14px] flex-1 text-left"
                style={labelColor ? { color: labelColor } : undefined}
              >
                {label}
              </span>
              {isCompleted && !isActive && (
                <div className="mr-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </span>
          </div>
        </div>
      );
    })}
  </nav>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CreateServiceRequest: React.FC = () => {
  const {
    step,
    maxStep,
    serviceType,
    serviceTypeLoading,
    clientInfo,
    clientInfoLoading,
    facilities,
    facilitiesLoading,
    facilitiesHasNextPage,
    selectedFacilityId,
    selectedFacility,
    facilityError,
    canListFacilities,
    privilegesLoading,
    equipment,
    equipmentLoading,
    equipmentHasNextPage,
    selectedEquipmentId,
    selectedEquipment,
    canListEquipment,
    isEquipmentLoadingForFacility,
    description,
    descriptionError,
    step2ValidationAttempt,
    images,
    imageUploadProgress,
    imagesError,
    document,
    documentUploadProgress,
    documentError,
    isUploading,
    isSubmitting,
    submitError,
    handleFacilitySelect,
    handleFacilitySearchChange,
    handleEquipmentSelect,
    handleEquipmentSearchChange,
    handleDescriptionChange,
    handleImagesSelect,
    handleImagesError,
    handleDocumentSelect,
    handleDocumentError,
    handleFacilitiesScrollEnd,
    handleEquipmentScrollEnd,
    handleStepClick,
    handleNext,
    handleBack,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleSubmit,
    handleCloseSubmitError,
    isCancelModalOpen,
  } = useCreateServiceRequest();

  const isLoading = serviceTypeLoading || privilegesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader text="Ładowanie danych Zamów usługę" />
      </div>
    );
  }

  const submitLabel = isSubmitting
    ? L.BUTTON_SUBMITTING
    : step === 3
      ? L.BUTTON_SUBMIT
      : L.BUTTON_NEXT;

  const handleActionButton = () => {
    if (step === 3) {
      void handleSubmit();
    } else {
      handleNext();
    }
  };

  return (
    <div className="relative w-full h-full">
      <StepperPageShell
        activeStep={step}
        title={L.PAGE_TITLE}
        stepper={
          <StepTabNav
            currentStep={step}
            maxStep={maxStep}
            onStepClick={handleStepClick}
          />
        }
        footer={
          <>
            <Button
              label={L.BUTTON_CANCEL}
              buttonType="Square"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
            />
            <div className="flex gap-4">
              {step > 1 && (
                <Button
                  label={L.BUTTON_BACK}
                  buttonType="Square"
                  type="button"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  className="!bg-transparent !h-[40px] cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
                />
              )}
              <Button
                label={submitLabel}
                type="button"
                onClick={handleActionButton}
                disabled={isSubmitting || isUploading}
                className="!h-[40px] text-labelLarge cursor-pointer !rounded-[8px]"
              />
            </div>
          </>
        }
      >
        {step === 1 && (
          <Step1FacilityEquipment
            serviceType={serviceType}
            serviceTypeLoading={serviceTypeLoading}
            facilities={facilities}
            facilitiesLoading={facilitiesLoading}
            facilitiesHasNextPage={facilitiesHasNextPage}
            selectedFacilityId={selectedFacilityId}
            selectedFacilityName={selectedFacility?.name ?? ''}
            facilityError={facilityError}
            canListFacilities={canListFacilities}
            privilegesLoading={privilegesLoading}
            onFacilitySearchChange={handleFacilitySearchChange}
            equipment={equipment}
            equipmentLoading={equipmentLoading}
            equipmentHasNextPage={equipmentHasNextPage}
            selectedEquipmentId={selectedEquipmentId}
            selectedEquipmentName={selectedEquipment?.name ?? ''}
            canListEquipment={canListEquipment}
            isEquipmentLoadingForFacility={isEquipmentLoadingForFacility}
            onFacilitySelect={handleFacilitySelect}
            onEquipmentSelect={handleEquipmentSelect}
            onEquipmentSearchChange={handleEquipmentSearchChange}
            onFacilitiesScrollEnd={handleFacilitiesScrollEnd}
            onEquipmentScrollEnd={handleEquipmentScrollEnd}
          />
        )}

        {step === 2 && (
          <Step2DetailsAttachments
            description={description}
            descriptionError={descriptionError}
            step2ValidationAttempt={step2ValidationAttempt}
            onDescriptionChange={handleDescriptionChange}
            images={images}
            imageUploadProgress={imageUploadProgress}
            imagesError={imagesError}
            onImagesSelect={handleImagesSelect}
            onImagesError={handleImagesError}
            document={document}
            documentUploadProgress={documentUploadProgress}
            documentError={documentError}
            onDocumentSelect={handleDocumentSelect}
            onDocumentError={handleDocumentError}
            isSubmitting={isSubmitting}
          />
        )}

        {step === 3 && (
          <Step3Preview
            serviceType={serviceType}
            clientInfo={clientInfo}
            clientInfoLoading={clientInfoLoading}
            selectedFacility={selectedFacility}
            selectedEquipment={selectedEquipment}
            description={description}
            images={images}
            document={document}
          />
        )}
      </StepperPageShell>

      <Snackbar
        open={Boolean(submitError)}
        message={submitError}
        variant="error"
        onClose={handleCloseSubmitError}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />

      <CreateModal
        open={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirmCancel={handleConfirmCancel}
      />
    </div>
  );
};

export default CreateServiceRequest;
